import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { animate, stagger } from 'animejs';
import { supabase } from '../lib/supabase';
import { AuroraCanvas } from '../components/ui/AuroraCanvas';
import { LogoHalo } from '../components/ui/LogoHalo';
import { OrbionCredit } from '../components/ui/OrbionCredit';

function Shell({ titulo, descricao, children }) {
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!wrapRef.current) return;
        animate(wrapRef.current.querySelectorAll('[data-stagger]'), {
            opacity: [0, 1],
            translateY: [18, 0],
            duration: 720,
            delay: stagger(75, { start: 60 }),
            ease: 'cubicBezier(0.16, 1, 0.3, 1)',
        });
    }, []);

    return (
        <AuroraCanvas>
            <div ref={wrapRef} style={{ width: '100%', maxWidth: '400px' }}>
                <div data-stagger style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem', opacity: 0 }}>
                    <LogoHalo logoSize={88} frameWidth={360} frameHeight={132} />
                </div>
                <div data-stagger style={{ display: 'flex', justifyContent: 'center', marginBottom: '2.25rem', opacity: 0 }}>
                    <OrbionCredit size="md" />
                </div>

                <div data-stagger className="glass-card" style={{ padding: '1.85rem', opacity: 0 }}>
                    <div style={{
                        fontSize: '1.05rem',
                        fontWeight: 700,
                        color: 'var(--ink)',
                        letterSpacing: '-0.005em',
                        marginBottom: '0.25rem',
                    }}>
                        {titulo}
                    </div>
                    {descricao && (
                        <div style={{
                            fontSize: '0.78rem',
                            color: 'var(--muted)',
                            marginBottom: '1.5rem',
                        }}>
                            {descricao}
                        </div>
                    )}
                    {children}
                </div>
            </div>
        </AuroraCanvas>
    );
}

function SolicitarReset() {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus(null);
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/redefinir-senha`,
        });
        setLoading(false);
        if (error) {
            setStatus({ tipo: 'erro', msg: error.message || 'Não foi possível enviar o email.' });
        } else {
            setStatus({ tipo: 'ok', msg: 'Se existir uma conta com este email, um link de redefinição foi enviado.' });
        }
    };

    return (
        <Shell titulo="Redefinir senha" descricao="Enviaremos um link seguro para você definir uma nova senha.">
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.95rem' }}>
                <div>
                    <label className="field-label" htmlFor="reset-email">Email</label>
                    <div style={{ position: 'relative' }}>
                        <Icon icon="ph:envelope-simple-bold" width={16} height={16}
                              style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--dim)', pointerEvents: 'none' }} />
                        <input id="reset-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                               required placeholder="seu@email.com" autoComplete="email" className="input" style={{ paddingLeft: '2.4rem' }} />
                    </div>
                </div>

                {status && (
                    <div style={{
                        background: status.tipo === 'ok' ? 'rgba(0, 214, 143, 0.06)' : 'rgba(239, 68, 68, 0.08)',
                        border: `1px solid ${status.tipo === 'ok' ? 'rgba(0, 214, 143, 0.22)' : 'rgba(239, 68, 68, 0.20)'}`,
                        borderRadius: '10px',
                        padding: '0.6rem 0.85rem',
                        fontSize: '0.78rem',
                        color: status.tipo === 'ok' ? 'var(--emerald-soft)' : 'var(--danger-soft)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}>
                        <Icon icon={status.tipo === 'ok' ? 'ph:check-circle-bold' : 'ph:warning-circle-bold'} width={15} height={15} />
                        {status.msg}
                    </div>
                )}

                <button type="submit" disabled={loading}
                        className="pill pill-primary pill-shimmer"
                        style={{ width: '100%', padding: '0.75rem', marginTop: '0.4rem', fontSize: '0.875rem' }}>
                    {loading ? (
                        <><Icon icon="svg-spinners:180-ring-with-bg" width={15} height={15} /> Enviando...</>
                    ) : (
                        <>Enviar link <Icon icon="ph:paper-plane-tilt-bold" width={15} height={15} /></>
                    )}
                </button>

                <Link to="/" style={{
                    fontSize: '0.75rem', color: 'var(--dim)', textAlign: 'center', textDecoration: 'none',
                    marginTop: '0.25rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem',
                    transition: 'color 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--muted)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dim)'; }}>
                    <Icon icon="ph:arrow-left-bold" width={13} height={13} />
                    Voltar ao login
                </Link>
            </form>
        </Shell>
    );
}

function DefinirNovaSenha() {
    const navigate = useNavigate();
    const [senha, setSenha] = useState('');
    const [confirmar, setConfirmar] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (senha.length < 6) { setError('Senha precisa de no mínimo 6 caracteres.'); return; }
        if (senha !== confirmar) { setError('As senhas não conferem.'); return; }
        setLoading(true);
        const { error } = await supabase.auth.updateUser({ password: senha });
        setLoading(false);
        if (error) { setError(error.message || 'Não foi possível atualizar a senha.'); return; }
        navigate('/', { replace: true });
    };

    return (
        <Shell titulo="Definir nova senha" descricao="Escolha uma senha forte com pelo menos 6 caracteres.">
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.95rem' }}>
                <div>
                    <label className="field-label">Nova senha</label>
                    <div style={{ position: 'relative' }}>
                        <Icon icon="ph:lock-simple-bold" width={16} height={16}
                              style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--dim)', pointerEvents: 'none' }} />
                        <input type="password" value={senha} onChange={(e) => setSenha(e.target.value)}
                               required placeholder="••••••••" autoComplete="new-password" className="input" style={{ paddingLeft: '2.4rem' }} />
                    </div>
                </div>
                <div>
                    <label className="field-label">Confirmar senha</label>
                    <div style={{ position: 'relative' }}>
                        <Icon icon="ph:lock-key-bold" width={16} height={16}
                              style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--dim)', pointerEvents: 'none' }} />
                        <input type="password" value={confirmar} onChange={(e) => setConfirmar(e.target.value)}
                               required placeholder="••••••••" autoComplete="new-password" className="input" style={{ paddingLeft: '2.4rem' }} />
                    </div>
                </div>

                {error && (
                    <div style={{
                        background: 'rgba(239, 68, 68, 0.08)',
                        border: '1px solid rgba(239, 68, 68, 0.20)',
                        borderRadius: '10px',
                        padding: '0.6rem 0.85rem',
                        fontSize: '0.78rem',
                        color: 'var(--danger-soft)',
                        display: 'flex', alignItems: 'center', gap: '0.5rem',
                    }}>
                        <Icon icon="ph:warning-circle-bold" width={15} height={15} />
                        {error}
                    </div>
                )}

                <button type="submit" disabled={loading}
                        className="pill pill-primary pill-shimmer"
                        style={{ width: '100%', padding: '0.75rem', marginTop: '0.4rem', fontSize: '0.875rem' }}>
                    {loading ? (
                        <><Icon icon="svg-spinners:180-ring-with-bg" width={15} height={15} /> Salvando...</>
                    ) : (
                        <>Salvar nova senha <Icon icon="ph:check-bold" width={15} height={15} /></>
                    )}
                </button>
            </form>
        </Shell>
    );
}

export default function RedefinirSenha({ session }) {
    return session ? <DefinirNovaSenha /> : <SolicitarReset />;
}
